import { Auth0Provider } from '@auth0/auth0-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { VITE_AUTH0_AUDIENCE, VITE_AUTH0_CLIENT_ID, VITE_AUTH0_DOMAIN } from '@/utils/envs';

import type { AppState } from '@auth0/auth0-react';
import type { PropsWithChildren } from 'react';

/**
 * Configures the Auth0 SPA context for the whole application.
 *
 * The access token is requested for `VITE_AUTH0_AUDIENCE`, which must match
 * the API audience the punto99 rewrite validates. Without the correct
 * audience the token is rejected by the `/admin/*` endpoints.
 */
export function AuthProvider(props: PropsWithChildren): React.JSX.Element {
  const navigate = useNavigate();

  const isMissingConfiguration =
    !VITE_AUTH0_DOMAIN || !VITE_AUTH0_CLIENT_ID || !VITE_AUTH0_AUDIENCE;

  if (isMissingConfiguration) {
    return (
      <div>
        Authentication configuration error: Missing Auth0 environment variables. Please ensure
        VITE_AUTH0_DOMAIN, VITE_AUTH0_CLIENT_ID and VITE_AUTH0_AUDIENCE are properly set.
      </div>
    );
  }

  function onRedirectCallback(appState?: AppState): void {
    const defaultPath = window.location.pathname;
    const targetPath = appState?.returnTo;

    if (targetPath) {
      navigate(targetPath);
    } else {
      navigate(defaultPath);
    }
  }

  return (
    <Auth0Provider
      domain={VITE_AUTH0_DOMAIN}
      clientId={VITE_AUTH0_CLIENT_ID}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      authorizationParams={{
        audience: VITE_AUTH0_AUDIENCE,
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
    >
      {props.children}
    </Auth0Provider>
  );
}
